import classnames from 'classnames';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { STOCK_AUDIT_CANCELLED, STOCK_AUDIT_UPDATED } from '../../../../../constants/analytics-events';
import { useLuxonUserDateFormat, useUtcFormattedDate } from '../../../../../hooks/use-datetime-formats';
import { useModal } from '../../../../../hooks/use-modal';
import { useSendMetrics } from '../../../../../hooks/use-send-metrics';
import messages from '../../../messages';
import { cancelStockAudit, StockAuditStatus, updateStockAudit } from '../../stock-audit/stock-audits-api';
import { useInventoryContext } from '../../inventory-context';
import { AuditDetailsModal } from '../../stock-audit/components/audit-details-modal/audit-details-modal';
import styles from './style.module.less';

type AuditRowData = {
  auditId?: string;
  auditName?: string;
  auditStatus?: string;
  auditDueDateUtc?: string;
};

const VALID_AUDIT_STATUSES = new Set<string>(Object.values(StockAuditStatus));

const isValidAuditStatus = (status: string | undefined): status is StockAuditStatus => {
  return !!status && VALID_AUDIT_STATUSES.has(status);
};

const statusMessageMap: Record<StockAuditStatus, { defaultMessage: string; description: string }> = {
  [StockAuditStatus.assigned]: messages.auditStatusAssigned,
  [StockAuditStatus.inProgress]: messages.auditStatusInProgress,
};

type StatusVariant = 'assigned' | 'inProgress' | 'overdue';

const statusContainerClassMap: Record<StatusVariant, string> = {
  assigned: styles['status-container-assigned'],
  inProgress: styles['status-container-in-progress'],
  overdue: styles['status-container-overdue'],
};

const statusDotClassMap: Record<StatusVariant, string> = {
  assigned: styles['status-dot-assigned'],
  inProgress: styles['status-dot-in-progress'],
  overdue: styles['status-dot-overdue'],
};

const resolveStatusVariant = (status: StockAuditStatus, dueDate: DateTime | null): StatusVariant => {
  if (dueDate && dueDate < DateTime.now()) {
    return 'overdue';
  }
  return status === StockAuditStatus.assigned ? 'assigned' : 'inProgress';
};

type AuditCellProps = {
  auditId: string;
  auditName: string;
  auditStatus: StockAuditStatus;
  auditDueDateUtc?: string;
};

const AuditCell = ({ auditId, auditName, auditStatus, auditDueDateUtc }: AuditCellProps) => {
  const intl = useIntl();
  const { isOpen: isModalOpen, open: openModal, close: closeModal } = useModal();
  const luxonDateFormat = useLuxonUserDateFormat();
  const { refreshGridData } = useInventoryContext();
  const trackMetrics = useSendMetrics();
  const [isSaving, setIsSaving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const dueDate = auditDueDateUtc ? DateTime.fromISO(auditDueDateUtc, { zone: 'utc' }) : null;
  const formattedDueDate = useUtcFormattedDate(auditDueDateUtc ?? '');
  const statusVariant = resolveStatusVariant(auditStatus, dueDate);
  const isAuditEditable = auditStatus === StockAuditStatus.assigned;

  const handleSave = useCallback(
    async ({
      auditName: updatedAuditName,
      dueDate: updatedDueDate,
      assigneeUserId: updatedAssigneeUserId,
      notes: updatedNotes,
    }: {
      auditName: string;
      dueDate: string;
      assigneeUserId: string;
      notes: string;
    }) => {
      setIsSaving(true);
      try {
        await updateStockAudit(
          {
            auditId,
            auditName: updatedAuditName,
            dueDate: updatedDueDate,
            assigneeUserId: updatedAssigneeUserId,
            notes: updatedNotes,
          },
          luxonDateFormat,
        );
        trackMetrics(STOCK_AUDIT_UPDATED, { auditId });
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.auditUpdateSuccess} />,
          notificationType: NOTIFICATION_TYPE_SUCCESS,
        });
        refreshGridData();
        return true;
      } catch {
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.auditUpdateError} />,
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [auditId, luxonDateFormat, refreshGridData, trackMetrics],
  );

  const handleCancelAudit = useCallback(async () => {
    setIsCancelling(true);
    try {
      await cancelStockAudit(auditId);
      trackMetrics(STOCK_AUDIT_CANCELLED, { auditId });
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.auditCancelSuccess} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
      });
      refreshGridData();
      return true;
    } catch {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.auditCancelError} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
      return false;
    } finally {
      setIsCancelling(false);
    }
  }, [auditId, refreshGridData, trackMetrics]);

  return (
    <>
      <Button
        ariaLabel={intl.formatMessage(statusMessageMap[auditStatus])}
        className={classnames(styles['status-container'], statusContainerClassMap[statusVariant])}
        onClick={openModal}
        theme={THEME_CONSTANTS.unstyled}
      >
        <span className={classnames(styles['status-dot'], statusDotClassMap[statusVariant])} />
        <div className={styles['status-body']}>
          <span className={styles['audit-name']}>{auditName}</span>
          {formattedDueDate ? (
            <span className={styles['due-date']}>
              <FormattedMessage {...messages.auditDueDateLabel} values={{ dueDate: formattedDueDate }} />
            </span>
          ) : null}
        </div>
      </Button>
      {isModalOpen ? (
        <AuditDetailsModal
          auditId={auditId}
          auditName={auditName}
          dueDateIso={auditDueDateUtc ?? ''}
          isCancelling={isCancelling}
          isEditable={isAuditEditable}
          isSaving={isSaving}
          onCancelAudit={handleCancelAudit}
          onClose={closeModal}
          onSave={handleSave}
        />
      ) : null}
    </>
  );
};

const name = <FormattedMessage {...messages.auditColumnHeader} />;

export default {
  name,
  getMessageDescriptor: () => messages.auditColumnHeader,
  render: (data: AuditRowData) => {
    const auditStatus = data?.auditStatus;
    if (!data?.auditId || !data?.auditName || !isValidAuditStatus(auditStatus)) {
      return null;
    }
    return <AuditCell auditDueDateUtc={data.auditDueDateUtc} auditId={data.auditId} auditName={data.auditName} auditStatus={auditStatus} />;
  },
};
